import React, { useEffect, useState, useRef, Suspense, lazy, createElement } from 'react';
// Declare fbq global for TypeScript
declare global {
  interface Window {
    fbq: (...args: unknown[]) => void;
    _fbq: unknown;
  }
}
// Lazy load the heavy canvas — hero renders instantly without it
const MatrixCanvas = lazy(() => import('../components/MatrixCanvas').then(m => ({
  default: m.MatrixCanvas
})));
export function GamePage() {
  const [isLoaded, setIsLoaded] = useState(false);
  const [showCanvas, setShowCanvas] = useState(false);
  const revealRefs = useRef<(HTMLElement | null)[]>([]);
  useEffect(() => {
    // Inject Meta Pixel
    if (!window.fbq) {
      const script = document.createElement('script');
      script.innerHTML = `
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '1284151853554260');
      `;
      document.head.appendChild(script);
      // Noscript pixel fallback
      const noscript = document.createElement('noscript');
      noscript.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=1284151853554260&ev=PageView&noscript=1" />`;
      document.head.appendChild(noscript);
    }
    // Fire PageView
    window.fbq('track', 'PageView');
    // Hero renders immediately
    const heroTimer = setTimeout(() => setIsLoaded(true), 100);
    // Canvas loads after hero is visible
    const canvasTimer = setTimeout(() => setShowCanvas(true), 600);
    return () => {
      clearTimeout(heroTimer);
      clearTimeout(canvasTimer);
    };
  }, []);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '-40px'
    });
    revealRefs.current.forEach(el => {
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);
  const addToRefs = (el: HTMLElement | null) => {
    if (el && !revealRefs.current.includes(el)) revealRefs.current.push(el);
  };
  const handleAddToCart = (price: string) => {
    if (window.fbq) {
      window.fbq('track', 'AddToCart', {
        value: price,
        currency: 'USD'
      });
    }
  };
  return <div className="min-h-screen bg-[#020510] text-[#00ff41] font-['Exo_2',sans-serif] overflow-x-hidden relative scanlines" data-id="element-26">
      {/* Matrix Rain Background — deferred for fast initial paint */}
      {showCanvas && <Suspense fallback={null} data-id="element-27">
          <MatrixCanvas data-id="element-28" />
        </Suspense>}

      {/* Moving Scanline */}
      <div className="matrix-scanline" data-id="element-29" />

      {/* Green nebula tint */}
      <div className="fixed inset-0 pointer-events-none z-0" data-id="element-30">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_center,rgba(0,255,65,0.03)_0%,transparent_70%)]" data-id="element-31" />
      </div>

      {/* ═══════════════════════════════════════
                                    SECTION 1 — HERO
                                ═══════════════════════════════════════ */}
      <section className="min-h-screen flex flex-col justify-center items-center relative px-6 py-20 z-10" data-id="element-32">
        <div className={`text-center transition-all duration-[1800ms] ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} data-id="element-33">
          {/* Terminal boot sequence */}
          <div className="mb-10 text-left inline-block font-['Share_Tech_Mono',monospace]" data-id="element-34">
            <p className="text-[#006600] text-xs tracking-[3px] mb-1" data-id="element-35">
              &gt; SISTEMA INICIANDO...
            </p>
            <p className="text-[#006600] text-xs tracking-[3px] mb-1" data-id="element-36">
              &gt; CONEXIÓN ESTABLECIDA
            </p>
            <p className="text-[#00aa00] text-xs tracking-[3px] mb-1" data-id="element-37">
              &gt; PROTOCOLO MATRIX ACTIVO
            </p>
            <p className="text-[#00ff41] text-xs tracking-[3px] matrix-cursor" data-id="element-38">
              &gt; BIENVENIDO AL TABLERO
            </p>
          </div>

          {/* Main Headline */}
          <h1 className="font-['Orbitron',monospace] font-black leading-[1.05] tracking-tight mb-8" style={{
          fontSize: 'clamp(2.2rem, 8vw, 6rem)'
        }} data-id="element-39">
            <span className="block text-[#00ff41] matrix-glow" data-id="element-40">
              ¿ESTÁS LISTO
            </span>
            <span className="block text-[#00ff9f]" style={{
            textShadow: '0 0 30px #00ff9f'
          }} data-id="element-41">
              PARA JUGAR?
            </span>
          </h1>

          {/* Subheadline */}
          <p className="text-[#00aa00] text-lg md:text-xl max-w-xl mx-auto mb-10 leading-relaxed" data-id="element-42">
            No estás viviendo.
            <br data-id="element-43" />
            <span className="text-[#00ff41]" data-id="element-44">
              Estás ejecutando un programa.
            </span>
          </p>

          {/* Separator */}
          <div className="flex items-center justify-center gap-3 mb-10" data-id="element-45">
            <div className="flex-1 max-w-[80px] h-px bg-[#00ff41]/40" data-id="element-46" />
            <span className="text-[#00ff41] text-xs" data-id="element-47">◆</span>
            <div className="flex-1 max-w-[80px] h-px bg-[#00ff41]/40" data-id="element-48" />
          </div>

          {/* CTA */}
          <a href="#pildoras" className="inline-block px-10 py-4 border border-[#00ff41] text-[#00ff41] text-sm tracking-[4px] uppercase transition-all duration-300 hover:bg-[#00ff41]/10 matrix-btn-pulse" data-id="element-49">
            &gt; DESPERTAR AHORA
          </a>

          <p className="mt-6 text-[#003300] text-[10px] tracking-[3px] uppercase" data-id="element-50">
            [ NO ES PARA TODOS ]
          </p>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 opacity-40" data-id="element-51">
          <div className="w-px h-12 bg-gradient-to-b from-[#00ff41] to-transparent animate-slow-pulse" data-id="element-52" />
        </div>
      </section>

      {/* ═══════════════════════════════════════
                                    SECTION 2 — LAS DOS PÍLDORAS
                                ═══════════════════════════════════════ */}
      <section id="pildoras" className="min-h-screen flex items-center justify-center px-6 py-24 relative z-10 bg-[rgba(0,0,0,0.6)]" data-id="element-53">
        <div ref={addToRefs} className="matrix-reveal max-w-4xl mx-auto" data-id="element-54">
          <p className="text-[#006600] text-[10px] tracking-[5px] uppercase mb-8 text-center" data-id="element-55">
            &gt; MORPHEUS_PROTOCOL.exe
          </p>
          <p className="text-[#006600] text-[10px] tracking-[5px] uppercase mb-12 text-center" data-id="element-56">
            &gt; INICIANDO SECUENCIA DE DECISIÓN...
          </p>

          <blockquote className="text-center mb-16" data-id="element-57">
            <p className="text-[#00aa00] text-lg md:text-xl italic leading-relaxed max-w-2xl mx-auto" data-id="element-58">
              "Esta es tu última oportunidad.
              <br data-id="element-59" />
              Después de esto, no hay vuelta atrás."
            </p>
            <p className="text-[#003300] text-xs mt-4 tracking-widest" data-id="element-60">
              — MORPHEUS
            </p>
          </blockquote>

          {/* The Two Pills */}
          <div className="grid md:grid-cols-2 gap-6 mb-12" data-id="element-61">
            {/* Blue Pill */}
            <div className="bg-[rgba(0,20,80,0.5)] border border-[#0044cc]/60 p-8 blue-pill-glow" data-id="element-62">
              <div className="text-center mb-6" data-id="element-63">
                <div className="text-5xl mb-3" data-id="element-64">◉</div>
                <p className="text-[#0044cc] text-sm tracking-[4px] uppercase font-bold" data-id="element-65">
                  PÍLDORA AZUL
                </p>
              </div>
              <div className="space-y-3 text-center" data-id="element-66">
                <p className="text-[#0033aa]" data-id="element-67">Cierra esta página.</p>
                <p className="text-[#0033aa]" data-id="element-68">Sigue durmiendo.</p>
                <p className="text-[#0033aa]" data-id="element-69">El sistema te lo agradece.</p>
              </div>
              <p className="text-[#001144] text-[9px] tracking-[2px] uppercase text-center mt-8" data-id="element-70">
                &gt; FIN DE LA TRANSMISIÓN
              </p>
            </div>

            {/* Red Pill */}
            <div className="bg-[rgba(80,0,0,0.5)] border border-[#cc0000]/60 p-8 red-pill-glow flex flex-col" data-id="element-71">
              <div className="text-center mb-6" data-id="element-72">
                <div className="text-5xl mb-3 text-[#cc0000]" data-id="element-73">◉</div>
                <p className="text-[#cc0000] text-sm tracking-[4px] uppercase font-bold" data-id="element-74">
                  PÍLDORA ROJA
                </p>
              </div>
              <div className="space-y-3 text-center flex-1" data-id="element-75">
                <p className="text-[#aa2200]" data-id="element-76">Continúa leyendo.</p>
                <p className="text-[#cc3300]" data-id="element-77">
                  Descubre hasta dónde llega el conejo.
                </p>
                <p className="text-[#ff4400] font-bold" data-id="element-78">
                  El tablero se revela.
                </p>
              </div>
              <a href="#paquetes" className="block mt-8 px-6 py-3 border border-[#cc0000] text-[#cc0000] text-sm tracking-[3px] uppercase text-center transition-all duration-300 hover:bg-[#cc0000]/20 hover:shadow-[0_0_20px_rgba(204,0,0,0.5)]" data-id="element-79">
                &gt; ELEGIR ESTA PÍLDORA
              </a>
            </div>
          </div>

          <p className="text-[#003300] text-[10px] tracking-[3px] uppercase text-center animate-signal-flicker" data-id="element-80">
            &gt; DECISIÓN REGISTRADA...
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════════════
                                    SECTION 3 — EL DESPERTAR
                                ═══════════════════════════════════════ */}
      <section id="despertar" className="min-h-screen flex items-center justify-center px-6 py-24 relative z-10" data-id="element-81">
        <div ref={addToRefs} className="matrix-reveal max-w-2xl mx-auto text-center" data-id="element-82">
          <p className="text-[#006600] text-[10px] tracking-[5px] uppercase mb-12" data-id="element-83">
            &gt; SIGNAL_002 // EL_DESPERTAR.exe
          </p>

          <div className="space-y-6 text-lg md:text-xl text-[#00aa00] leading-relaxed" data-id="element-84">
            <p data-id="element-85">Siempre sentiste que algo no cuadraba.</p>
            <p className="text-[#003300]" data-id="element-86">— — — — — — —</p>
            <p data-id="element-87">Que trabajas… pero no avanzas.</p>
            <p className="text-[#003300]" data-id="element-88">— — — — — — —</p>
            <p data-id="element-89">Que deseas… pero nunca es suficiente.</p>
            <p className="text-[#003300]" data-id="element-90">— — — — — — —</p>
            <p data-id="element-91">
              Que temes…{' '}
              <span className="text-[#00ff41]" data-id="element-92">sin saber por qué.</span>
            </p>
          </div>

          <div className="flex items-center justify-center gap-4 my-16" data-id="element-93">
            <div className="flex-1 h-px bg-[#00ff41]/20" data-id="element-94" />
            <span className="text-[#00ff41]" data-id="element-95">◆</span>
            <div className="flex-1 h-px bg-[#00ff41]/20" data-id="element-96" />
          </div>

          <p className="text-[#00aa00] text-xl md:text-2xl mb-4" data-id="element-97">
            &gt; ANÁLISIS COMPLETO
          </p>
          <p className="text-[#00ff41] text-2xl md:text-3xl mb-2" data-id="element-98">
            Eso no es casualidad.
          </p>
          <p className="font-['Orbitron',monospace] font-black text-[#00ff41] matrix-glow" style={{
          fontSize: 'clamp(2rem, 6vw, 4rem)'
        }} data-id="element-99">
            Es arquitectura.
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════════════
                                    SECTION 4 — SISTEMA REVELADO
                                ═══════════════════════════════════════ */}
      <section className="min-h-screen flex items-center justify-center px-6 py-24 relative z-10 bg-[rgba(0,255,65,0.02)]" data-id="element-100">
        <div ref={addToRefs} className="matrix-reveal max-w-3xl mx-auto" data-id="element-101">
          <p className="text-[#006600] text-[10px] tracking-[5px] uppercase mb-12 text-center" data-id="element-102">
            &gt; SIGNAL_003 // SISTEMA_REVELADO
          </p>

          <div className="space-y-5 mb-20" data-id="element-103">
            {[{
            id: '01',
            white: 'El ego no eres tú.',
            green: 'Es interfaz.'
          }, {
            id: '02',
            white: 'El miedo no es natural.',
            green: 'Es frecuencia de control.'
          }, {
            id: '03',
            white: 'La distracción no es entretenimiento.',
            green: 'Es mantenimiento del sistema.'
          }, {
            id: '04',
            white: 'La narrativa no es información.',
            green: 'Es programación.'
          }].map(item => <div key={item.id} className="bg-[rgba(0,255,65,0.04)] border-l-2 border-[#00ff41] px-6 py-5" data-id="element-104">
                <p className="text-[#003300] text-[9px] tracking-[3px] uppercase mb-2" data-id="element-105">
                  &gt; DATO_{item.id}:
                </p>
                <p className="text-lg md:text-xl" data-id="element-106">
                  <span className="text-[#00aa00]" data-id="element-107">{item.white}</span>{' '}
                  <span className="text-[#00ff41] font-bold" data-id="element-108">{item.green}</span>
                </p>
              </div>)}
          </div>

          <div className="text-center py-12 border border-[#00ff41]/20 bg-[rgba(0,255,65,0.03)]" data-id="element-109">
            <p className="text-[#00aa00] text-2xl md:text-3xl mb-4" data-id="element-110">
              No eres el personaje.
            </p>
            <p className="font-['Orbitron',monospace] font-black text-[#00ff41] matrix-glow" style={{
            fontSize: 'clamp(2.5rem, 7vw, 5rem)'
          }} data-id="element-111">
              Eres el jugador.
            </p>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
                                    SECTION 5 — QUIÉNES SON ELLOS
                                ═══════════════════════════════════════ */}
      <section className="min-h-screen flex items-center justify-center px-6 py-24 relative z-10" data-id="element-112">
        <div ref={addToRefs} className="matrix-reveal max-w-3xl mx-auto" data-id="element-113">
          <p className="text-[#006600] text-[10px] tracking-[5px] uppercase mb-12 text-center" data-id="element-114">
            &gt; SIGNAL_004 // ARQUITECTURA_OCULTA
          </p>

          <div className="text-center mb-12 space-y-3" data-id="element-115">
            <p className="text-[#006600] text-lg md:text-xl" data-id="element-116">
              Existen sistemas que entendieron el código.
            </p>
            <p className="text-[#00aa00] text-lg md:text-xl" data-id="element-117">
              No te controlan con cadenas.
            </p>
            <p className="text-[#00ff41] text-xl md:text-2xl font-bold" data-id="element-118">
              Te controlan con percepción.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-12" data-id="element-119">
            {['Programación desde la infancia', 'Sistema de recompensa basado en miedo', 'Identidad impuesta', 'Deseo dirigido'].map((item, i) => <div key={i} className="bg-[rgba(0,255,65,0.03)] border-l-2 border-[#00ff41]/50 px-5 py-4" data-id="element-120">
                <p className="text-[#cc0000] text-[8px] tracking-[2px] uppercase mb-2" data-id="element-121">
                  [ CLASIFICADO ]
                </p>
                <p className="text-[#00aa00]" data-id="element-122">{item}</p>
              </div>)}
          </div>

          <div className="text-center" data-id="element-123">
            <p className="text-[#006600] text-xl" data-id="element-124">El tablero no es injusto.</p>
            <p className="text-[#00ff41] text-2xl md:text-3xl font-bold mt-2" data-id="element-125">
              Es estratégico.
            </p>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
                                    SECTION 6 — LOS PAQUETES
                                ═══════════════════════════════════════ */}
      <section id="paquetes" className="min-h-screen flex items-center justify-center px-6 py-24 relative z-10" data-id="element-126">
        <div ref={addToRefs} className="matrix-reveal max-w-4xl mx-auto" data-id="element-127">
          <p className="text-[#006600] text-[10px] tracking-[5px] uppercase mb-4 text-center" data-id="element-128">
            &gt; ACCESO CONCEDIDO
          </p>
          <p className="text-[#006600] text-[10px] tracking-[5px] uppercase mb-12 text-center" data-id="element-129">
            &gt; CARGANDO HERRAMIENTAS DE ACTIVACIÓN...
          </p>

          <div className="text-center mb-16" data-id="element-130">
            <p className="text-[#00aa00] text-xl md:text-2xl mb-2" data-id="element-131">
              Has elegido despertar.
            </p>
            <p className="font-['Orbitron',monospace] font-black text-[#00ff41] matrix-glow" style={{
            fontSize: 'clamp(1.5rem, 4vw, 2.5rem)'
          }} data-id="element-132">
              Selecciona tu nivel de acceso.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6" data-id="element-133">
            {/* Package 1 — Individual */}
            <div className="bg-[rgba(0,255,65,0.03)] border border-[#00ff41]/30 p-8 relative" data-id="element-134">
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#00ff41]/50 to-transparent" data-id="element-135" />
              <p className="text-[#006600] text-[9px] tracking-[3px] uppercase mb-4" data-id="element-136">
                &gt; PAQUETE_01 // INDIVIDUAL
              </p>
              <h3 className="font-['Orbitron',monospace] font-bold text-[#00ff41] text-xl mb-2" data-id="element-137">
                ¿ESTÁS LISTO PARA JUGAR?
              </h3>
              <p className="text-[#006600] text-[10px] tracking-[2px] uppercase mb-6" data-id="element-138">
                ¿Estás listo para jugar?
              </p>

              {/* Price — clear display */}
              <div className="bg-[rgba(0,255,65,0.06)] border border-[#00ff41]/20 px-5 py-4 mb-6" data-id="element-139">
                <p className="text-[#006600] text-[9px] tracking-[3px] uppercase mb-1" data-id="element-140">
                  &gt; PRECIO DE ACCESO
                </p>
                <div className="flex items-baseline gap-2" data-id="element-141">
                  <span className="font-['Orbitron',monospace] font-black text-[#00ff41]" style={{
                  fontSize: 'clamp(2.2rem, 5vw, 3rem)'
                }} data-id="element-142">
                    $17
                  </span>
                  <span className="text-[#00aa00] text-base font-bold" data-id="element-143">
                    USD
                  </span>
                </div>
                <p className="text-[#006600] text-[9px] tracking-[2px] uppercase mt-1" data-id="element-144">
                  pago único · acceso inmediato
                </p>
              </div>

              <div className="w-full h-px bg-[#00ff41]/20 mb-6" data-id="element-145" />
              <div className="space-y-3 mb-8" data-id="element-146">
                <div className="flex items-center gap-3" data-id="element-147">
                  <span className="text-[#00ff41]" data-id="element-148">◈</span>
                  <p className="text-[#00aa00]" data-id="element-149">1 LIBRO DIGITAL</p>
                </div>
                <div className="flex items-center gap-3" data-id="element-150">
                  <span className="text-[#00ff41]" data-id="element-151">◈</span>
                  <p className="text-[#00aa00]" data-id="element-152">1 AUDIOLIBRO</p>
                </div>
                <div className="flex items-center gap-3" data-id="element-153">
                  <span className="text-[#00ff41]" data-id="element-154">◈</span>
                  <p className="text-[#00aa00]" data-id="element-155">ACCESO INMEDIATO</p>
                </div>
              </div>
              <a href="https://oriammarketing.com/cart/45549347471558:1" target="_blank" rel="noopener noreferrer" onClick={() => handleAddToCart('17')} className="block px-6 py-3 border border-[#00ff41]/50 text-[#00ff41] text-sm tracking-[3px] uppercase text-center transition-all duration-300 hover:bg-[#00ff41]/10 hover:border-[#00ff41]" data-id="element-156">
                &gt; ACCEDER AHORA — $17 USD
              </a>
            </div>

            {/* Package 2 — Pack Matrix */}
            <div className="bg-[rgba(80,0,0,0.4)] border border-[#cc0000] p-8 relative red-pill-glow" data-id="element-157">
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-[#cc0000] via-[#ff4400] to-[#cc0000]" data-id="element-158" />
              <div className="absolute -top-3 left-1/2 -translate-x-1/2" data-id="element-159">
                <span className="bg-[#cc0000] text-[#fff] text-[9px] tracking-[2px] uppercase px-3 py-1 font-bold" data-id="element-160">
                  &gt; RECOMENDADO
                </span>
              </div>
              <p className="text-[#aa2200] text-[9px] tracking-[3px] uppercase mb-4 mt-2" data-id="element-161">
                &gt; PAQUETE_02 // PACK_MATRIX
              </p>
              <h3 className="font-['Orbitron',monospace] font-black text-[#ff4400] text-xl mb-2" data-id="element-162">
                PACK MATRIX
              </h3>
              <p className="text-[#cc3300] text-[10px] tracking-[2px] uppercase mb-6" data-id="element-163">
                Acceso Completo al Sistema
              </p>

              {/* Price — clear display */}
              <div className="bg-[rgba(120,0,0,0.3)] border border-[#cc0000]/40 px-5 py-4 mb-6" data-id="element-164">
                <p className="text-[#aa2200] text-[9px] tracking-[3px] uppercase mb-1" data-id="element-165">
                  &gt; PRECIO DE ACCESO
                </p>
                <div className="flex items-baseline gap-2" data-id="element-166">
                  <span className="font-['Orbitron',monospace] font-black text-[#ff4400]" style={{
                  fontSize: 'clamp(2.2rem, 5vw, 3rem)'
                }} data-id="element-167">
                    $37
                  </span>
                  <span className="text-[#cc3300] text-base font-bold" data-id="element-168">
                    USD
                  </span>
                </div>
                <p className="text-[#aa2200] text-[9px] tracking-[2px] uppercase mt-1" data-id="element-169">
                  pago único · acceso completo
                </p>
              </div>

              <div className="w-full h-px bg-[#cc0000]/30 mb-6" data-id="element-170" />
              <div className="space-y-3 mb-8" data-id="element-171">
                <div className="flex items-center gap-3" data-id="element-172">
                  <span className="text-[#ff4400]" data-id="element-173">◈</span>
                  <p className="text-[#ff4400] font-bold" data-id="element-174">2 LIBROS DIGITALES</p>
                </div>
                <div className="flex items-center gap-3" data-id="element-175">
                  <span className="text-[#ff4400]" data-id="element-176">◈</span>
                  <p className="text-[#ff4400] font-bold" data-id="element-177">2 AUDIOLIBROS</p>
                </div>
                <div className="flex items-center gap-3" data-id="element-178">
                  <span className="text-[#ff4400]" data-id="element-179">◈</span>
                  <p className="text-[#ff4400] font-bold" data-id="element-180">
                    MEDITACIONES GUIADAS
                  </p>
                </div>
                <div className="flex items-center gap-3" data-id="element-181">
                  <span className="text-[#ff4400]" data-id="element-182">◈</span>
                  <p className="text-[#ff4400] font-bold" data-id="element-183">MASTERCLASS</p>
                </div>
                <div className="w-full h-px bg-[#cc0000]/15 my-1" data-id="element-184" />
                <div className="flex items-center gap-3" data-id="element-185">
                  <span className="text-[#661100] text-[10px]" data-id="element-186">▸</span>
                  <p className="text-[#aa2200] text-sm" data-id="element-187">
                    ¿Estás listo para jugar?
                  </p>
                </div>
                <div className="flex items-center gap-3" data-id="element-188">
                  <span className="text-[#661100] text-[10px]" data-id="element-189">▸</span>
                  <p className="text-[#aa2200] text-sm" data-id="element-190">La Molécula de Dios</p>
                </div>
              </div>
              <a href="https://oriammarketing.com/cart/45549365919942:1" target="_blank" rel="noopener noreferrer" onClick={() => handleAddToCart('37')} className="block px-6 py-3 bg-[#cc0000] text-[#fff] text-sm tracking-[3px] uppercase text-center font-bold transition-all duration-300 hover:bg-[#ff2200] hover:shadow-[0_0_30px_rgba(204,0,0,0.7)]" data-id="element-191">
                &gt; ACTIVAR PACK MATRIX — $37 USD
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
                                    SECTION 7 — QUIEBRE PSICOLÓGICO
                                ═══════════════════════════════════════ */}
      <section className="min-h-[80vh] flex items-center justify-center px-6 py-24 relative z-10 bg-[rgba(0,0,0,0.7)]" data-id="element-192">
        <div ref={addToRefs} className="matrix-reveal max-w-lg mx-auto text-center" data-id="element-193">
          <p className="text-[#006600] text-[10px] tracking-[5px] uppercase mb-12" data-id="element-194">
            &gt; ADVERTENCIA FINAL...
          </p>

          <div className="space-y-10 text-lg md:text-xl text-[#006600]" data-id="element-195">
            <p data-id="element-196">Si no lo lees…</p>
            <div className="h-4" data-id="element-197" />
            <p className="text-[#00aa00]" data-id="element-198">
              Seguirás jugando sin saber que juegas.
            </p>
            <p className="text-[#00aa00]" data-id="element-199">
              Seguirás reaccionando en lugar de crear.
            </p>
            <p className="text-[#00ff41]" data-id="element-200">Seguirás siendo avatar.</p>
          </div>

          <div className="flex items-center justify-center gap-4 my-14" data-id="element-201">
            <div className="flex-1 h-px bg-[#00ff41]/15" data-id="element-202" />
            <span className="text-[#00ff41]" data-id="element-203">◆</span>
            <div className="flex-1 h-px bg-[#00ff41]/15" data-id="element-204" />
          </div>

          <p className="text-[#006600] text-[10px] tracking-[3px] uppercase mb-4 animate-signal-flicker" data-id="element-205">
            &gt; SIGNAL LOST... &gt; RECONNECTING...
          </p>
          <p className="text-[#00aa00] text-xl mb-4" data-id="element-206">Una vez que recuerdas…</p>
          <p className="font-['Orbitron',monospace] font-black text-[#00ff41] matrix-glow animate-signal-flicker" style={{
          fontSize: 'clamp(1.8rem, 5vw, 3rem)'
        }} data-id="element-207">
            No hay vuelta atrás.
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════════════
                                    SECTION 8 — FINAL CTA
                                ═══════════════════════════════════════ */}
      <section className="min-h-screen flex flex-col justify-center items-center relative px-6 py-24 z-10" data-id="element-208">
        <div ref={addToRefs} className="matrix-reveal text-center" data-id="element-209">
          <p className="text-[#006600] text-[10px] tracking-[5px] uppercase mb-4" data-id="element-210">
            &gt; TRANSMISIÓN FINAL
          </p>
          <p className="text-[#006600] text-[10px] tracking-[5px] uppercase mb-12" data-id="element-211">
            &gt; TABLERO ACTIVO
          </p>

          <p className="text-[#00aa00] text-xl md:text-2xl italic mb-8" data-id="element-212">
            El tablero ya está activo.
          </p>

          <h2 className="font-['Orbitron',monospace] font-black leading-[1.05] tracking-tight mb-12" style={{
          fontSize: 'clamp(2rem, 7vw, 5rem)'
        }} data-id="element-213">
            <span className="block text-[#00ff41] matrix-glow" data-id="element-214">
              ¿ESTÁS LISTO
            </span>
            <span className="block text-[#00ff9f]" style={{
            textShadow: '0 0 30px #00ff9f'
          }} data-id="element-215">
              PARA JUGAR?
            </span>
          </h2>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10" data-id="element-216">
            <a href="#paquetes" className="px-10 py-4 bg-[#00ff41] text-[#000] font-bold text-sm tracking-[3px] uppercase transition-all duration-300 hover:bg-[#00ff9f] hover:shadow-[0_0_40px_rgba(0,255,65,0.6)]" data-id="element-217">
              &gt; ACCEDER AHORA
            </a>
            <a href="#paquetes" className="px-10 py-4 border border-[#00ff41] text-[#00ff41] text-sm tracking-[3px] uppercase transition-all duration-300 hover:bg-[#00ff41]/10 matrix-border-glow" data-id="element-218">
              &gt; ACTIVAR PACK MATRIX
            </a>
          </div>

          <p className="text-[#003300] text-[11px] tracking-[2px] max-w-md mx-auto" data-id="element-219">
            &gt; El sistema no se rompe desde afuera.
            <br data-id="element-220" />
            <span className="text-[#006600]" data-id="element-221">
              &gt; Se reescribe desde adentro.
            </span>
          </p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-10 border-t border-[#00ff41]/10 text-center relative z-10" data-id="element-222">
        <p className="text-[#003300] text-[10px] tracking-[3px] uppercase mb-2" data-id="element-223">
          &gt; © 2025 · ¿ESTÁS LISTO PARA JUGAR? · TRANSMISSION ENDS
        </p>
        <p className="text-[#002200] text-[9px] tracking-[2px] matrix-cursor" data-id="element-224">
          &gt; [ MATRIX_PROTOCOL · SIGNAL_ACTIVE · SYSTEM_ONLINE ]
        </p>
      </footer>
    </div>;
}